import type { ContactFormRequest, FieldErrors } from '../types/contact';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export type SubmitResult =
  | { ok: true; message: string }
  | { ok: false; errors: FieldErrors };

/**
 * The API returns validation failures as RFC 7807 ProblemDetails:
 *   { title, status, errors: { "Name": ["The Name field is required."] } }
 *
 * Keys are the C# property names, so PascalCase rather than the camelCase the request used.
 * Matching is done case-insensitively against the fields we know about rather than by
 * transforming the case, because nothing guarantees the casing survives a framework upgrade.
 *
 * Two other key shapes appear when the body cannot be deserialized at all: a JSON path such as
 * "$.category", and the action parameter name "model". Their messages name .NET types and byte
 * offsets, so they are recognized and replaced rather than shown to a visitor.
 */
const FIELD_KEYS: (keyof ContactFormRequest)[] = [
  'name',
  'email',
  'phoneNumber',
  'okayTo',
  'category',
  'message',
  'numberOfChildren',
  'agesOfChildren',
  'captchaToken',
];

function toFieldErrors(problemErrors: Record<string, string[]>): FieldErrors {
  const errors: FieldErrors = {};

  for (const [rawKey, messages] of Object.entries(problemErrors)) {
    const message = messages?.[0];
    if (!message) continue;

    // "$.category" is a JSON path from the deserializer; "model" is the action parameter itself.
    const unreadable = rawKey.startsWith('$.') || rawKey.toLowerCase() === 'model';
    const bare = rawKey.replace(/^\$\./, '').toLowerCase();
    const field = FIELD_KEYS.find((key) => key.toLowerCase() === bare);

    if (unreadable) {
      // The server's own text names .NET types and byte offsets. Nothing a visitor can act on.
      const label = field ?? 'form';
      errors[label] = 'That value was not something I could read. Please check it and try again.';
    } else if (field === 'captchaToken') {
      // Nothing the visitor can act on — surface it as a form-level problem instead.
      errors.form = 'We could not confirm you are a person. Please reload the page and try again.';
    } else if (field) {
      errors[field] = message;
    } else {
      errors.form = message;
    }
  }

  return errors;
}

export async function submitContactForm(payload: ContactFormRequest): Promise<SubmitResult> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    return {
      ok: false,
      errors: {
        form:
          'We could not reach the server. Please check your connection and try again, or email' +
          ' me directly.',
      },
    };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A body is not guaranteed — fall through to the status-based handling below.
  }

  const parsed = body as {
    message?: string;
    errors?: Record<string, string[]>;
    // ProblemDetails puts the human-readable half on `detail`, not `message`.
    detail?: string;
  } | null;

  if (response.ok) {
    return { ok: true, message: parsed?.message ?? 'Thank you! I look forward to answering your email soon!' };
  }

  if (parsed?.errors) {
    return { ok: false, errors: toFieldErrors(parsed.errors) };
  }

  if (response.status === 429) {
    return {
      ok: false,
      errors: {
        form:
          'You have sent several messages in a short time. Please wait a little while before' +
          ' trying again, or email me directly.',
      },
    };
  }

  if (parsed?.detail) {
    return { ok: false, errors: { form: parsed.detail } };
  }

  return {
    ok: false,
    errors: {
      form: 'Something went wrong sending your message. Please try again in a moment, or email me directly.',
    },
  };
}
